import { ArrowLeft, CheckCircle, Eye, EyeOff, Plus, RefreshCw, Trash } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { graphql, useFragment, useLazyLoadQuery, useMutation } from "react-relay";
import { Link, useBlocker, useNavigate, useParams } from "react-router";
import { RequireScope } from "src/components/RequireScope";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { SecretInput } from "src/components/ui/secret-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { useMe } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import { useSettingsMobileHeader } from "src/pages/SettingsPage/hooks/useSettingsMobileHeader";
import { EditMcpServerRoute_server$key } from "src/pages/SettingsPage/routes/__generated__/EditMcpServerRoute_server.graphql";
import { EditMcpServerRouteQuery } from "src/pages/SettingsPage/routes/__generated__/EditMcpServerRouteQuery.graphql";
import { EditMcpServerRouteTestMutation } from "src/pages/SettingsPage/routes/__generated__/EditMcpServerRouteTestMutation.graphql";
import { EditMcpServerRouteTestOverridesMutation } from "src/pages/SettingsPage/routes/__generated__/EditMcpServerRouteTestOverridesMutation.graphql";
import { EditMcpServerRouteUpdateMutation, UpdateCustomMcpServerInput } from "src/pages/SettingsPage/routes/__generated__/EditMcpServerRouteUpdateMutation.graphql";
import { buildMcpCredentials, createHeaderEntry, HeaderEntryType, MCP_SERVERS_PATH, redirectToOAuth } from "src/pages/SettingsPage/routes/mcp-server-utils";
import McpOauthAdvancedSettings from "src/pages/SettingsPage/routes/McpOauthAdvancedSettings";
import { getKnowledgeBasePath, getProductName } from "src/utils/brandTheme";

const editMcpServerRouteQuery = graphql`
  query EditMcpServerRouteQuery($serverId: GlobalID!) {
    node(id: $serverId) {
      ...EditMcpServerRoute_server
    }
  }
`;

const editMcpServerRouteFragment = graphql`
  fragment EditMcpServerRoute_server on GraphQLCustomMcpServer {
    id
    name
    slug
    description
    url
    authType
    isActive
    discoveredTools
    toolCount
    resourceCount
    tokenAuthMethodOverride
    isOauthAuthorized
  }
`;

const updateServerMutation = graphql`
  mutation EditMcpServerRouteUpdateMutation($input: UpdateCustomMcpServerInput!) {
    updateCustomMcpServer(input: $input) {
      server {
        id
        name
        description
        url
        authType
        isActive
        discoveredTools
        toolCount
      }
      error
    }
  }
`;

const testConnectionMutation = graphql`
  mutation EditMcpServerRouteTestMutation($id: GlobalID!) {
    testCustomMcpServerConnection(id: $id) {
      success
      tools
      error
      oauthUrl
    }
  }
`;

const testOverridesMutation = graphql`
  mutation EditMcpServerRouteTestOverridesMutation($input: TestCustomMcpServerOverridesInput!) {
    testCustomMcpServerOverrides(input: $input) {
      success
      tools
      error
    }
  }
`;

export default function EditMcpServerRoute() {
  return (
    <RequireScope scope="write:integrations">
      <EditMcpServerRouteContent />
    </RequireScope>
  );
}

function EditMcpServerRouteContent() {
  useSettingsMobileHeader("Edit MCP Server");
  const navigate = useNavigate();
  const { serverId } = useParams<{ serverId: string }>();
  const { toast } = useToast();
  const me = useMe();
  const brandTheme = me.organization?.brandTheme ?? "TIERZERO";

  const data = useLazyLoadQuery<EditMcpServerRouteQuery>(editMcpServerRouteQuery, {
    serverId: serverId || "",
  });

  const [commitUpdate, isUpdating] = useMutation<EditMcpServerRouteUpdateMutation>(updateServerMutation);
  const [commitTest] = useMutation<EditMcpServerRouteTestMutation>(testConnectionMutation);
  const [commitTestOverrides] = useMutation<EditMcpServerRouteTestOverridesMutation>(testOverridesMutation);

  const server = useFragment<EditMcpServerRoute_server$key>(
    editMcpServerRouteFragment,
    data.node as EditMcpServerRoute_server$key | null,
  );

  const [name, setName] = useState(server?.name || "");
  const [description, setDescription] = useState(server?.description || "");
  const [url, setUrl] = useState(server?.url || "");
  const [authType, setAuthType] = useState(server?.authType || "API_KEY");
  const [apiKey, setApiKey] = useState("");
  const [headers, setHeaders] = useState<HeaderEntryType[]>([createHeaderEntry()]);
  const [urlOrAuthChanged, setUrlOrAuthChanged] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);
  const [testStatus, setTestStatus] = useState<{ status: "idle" | "testing" | "success" | "failed"; message: string | null }>({ status: "idle", message: null });
  const [oauthMethodOverride, setOauthMethodOverride] = useState(server?.tokenAuthMethodOverride ?? "auto");
  const skipBlockRef = useRef(false);

  // Credentials are considered changed only if the user entered non-empty values
  const hasNewCredentials =
    (authType === "API_KEY" && apiKey.trim() !== "") ||
    (authType === "CUSTOM_HEADERS" && headers.some((h) => h.key.trim() || h.value.trim()));
  const connectionChanged = urlOrAuthChanged || hasNewCredentials;
  const hasIncompleteHeaders =
    authType === "CUSTOM_HEADERS" &&
    headers.some((h) => (h.key.trim() && !h.value.trim()) || (!h.key.trim() && h.value.trim()));

  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  const isDirty =
    name !== (server?.name || "") ||
    description !== (server?.description || "") ||
    connectionChanged;

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (skipBlockRef.current) return false;
    if (!isDirty) return false;
    if (currentLocation.pathname === nextLocation.pathname) return false;
    return true;
  });

  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowUnsavedDialog(true);
    }
  }, [blocker.state]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
      return;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const handleTestConnection = useCallback(() => {
    if (!server?.id) return;
    setTestStatus({ status: "testing", message: null });

    if (connectionChanged) {
      const credentials = buildMcpCredentials(authType, apiKey, headers);
      commitTestOverrides({
        variables: {
          input: {
            id: server.id,
            url,
            authType,
            credentials: credentials ?? null,
          },
        },
        onCompleted: (response) => {
          const result = response.testCustomMcpServerOverrides;
          if (result?.success) {
            setTestStatus({
              status: "success",
              message: `Connected — ${result.tools?.length ?? 0} tool${(result.tools?.length ?? 0) !== 1 ? "s" : ""} discovered`,
            });
          } else {
            setTestStatus({ status: "failed", message: result?.error ?? "Connection failed" });
          }
        },
        onError: (error) => {
          setTestStatus({ status: "failed", message: error.message });
        },
      });
    } else {
      commitTest({
        variables: { id: server.id },
        onCompleted: (response) => {
          const result = response.testCustomMcpServerConnection;
          if (result?.oauthUrl) {
            redirectToOAuth(result.oauthUrl, toast);
            return;
          }
          if (result?.success) {
            setTestStatus({
              status: "success",
              message: `Connected — ${result.tools?.length ?? 0} tool${(result.tools?.length ?? 0) !== 1 ? "s" : ""} discovered`,
            });
          } else {
            setTestStatus({ status: "failed", message: result?.error ?? "Connection failed" });
          }
        },
        onError: (error) => {
          setTestStatus({ status: "failed", message: error.message });
        },
      });
    }
  }, [server?.id, connectionChanged, apiKey, headers, url, authType, commitTestOverrides, commitTest, toast]);

  const handleUpdate = useCallback(() => {
    if (!server?.id) return;
    const credentials = buildMcpCredentials(authType, apiKey, headers);
    const input: UpdateCustomMcpServerInput = {
      id: server.id,
      name: name.trim(),
      description: description.trim() || null,
      ...(connectionChanged && {
        url,
        authType,
        ...(credentials ? { credentials } : {}),
      }),
    };
    commitUpdate({
      variables: { input },
      onCompleted: (response) => {
        const result = response.updateCustomMcpServer;
        if (result?.error && !result.server) {
          toast({ title: "Error", description: result.error, variant: "error" });
        } else {
          if (result?.error) {
            toast({ title: "Server updated", description: result.error, variant: "error" });
          } else {
            toast({ title: "Server updated", description: `${name} has been updated.` });
          }
          skipBlockRef.current = true;
          navigate(MCP_SERVERS_PATH);
        }
      },
      onError: (error) => {
        toast({ title: "Error", description: error.message, variant: "error" });
      },
    });
  }, [server?.id, name, description, connectionChanged, url, authType, apiKey, headers, commitUpdate, toast, navigate]);

  const canSave =
    !!name.trim() &&
    !!description.trim() &&
    !!url.trim() &&
    !hasIncompleteHeaders &&
    (!connectionChanged || authType === "OAUTH" || testStatus.status === "success");

  if (!server) {
    return (
      <div className="flex-1 px-4 md:px-5 pt-4 pb-5">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(MCP_SERVERS_PATH)}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-muted-foreground" />
          </button>
          <h1 className="text-xl font-semibold text-foreground">Edit MCP Server</h1>
        </div>
        <div className="rounded-lg border border-border bg-card/30 p-8 text-center max-w-4xl">
          <p className="text-sm text-muted-foreground">
            MCP server not found. It may have been deleted or you don't have permission to access it.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate(MCP_SERVERS_PATH)}
          >
            Back to MCP Servers
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 px-4 md:px-5 pt-4 pb-5">
      {/* Header with back button */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(MCP_SERVERS_PATH)}
          className="p-2 hover:bg-accent rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-muted-foreground" />
        </button>
        <h1 className="text-xl font-semibold text-foreground">Edit MCP Server</h1>
      </div>

      <div className="max-w-4xl space-y-6">
        {/* Basic Info Section */}
        <div className="bg-card/30 rounded-lg p-6 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="mcp-server-name">Server Name <span className="text-red-500">*</span></Label>
            <Input
              id="mcp-server-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. My MCP Server"
              className="max-w-md"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="mcp-server-description">Description <span className="text-red-500">*</span></Label>
            <p className="text-muted-foreground text-sm">
              Describe what this MCP server handles. {getProductName(brandTheme)} uses this to decide when to use tools from this MCP server.
            </p>
            <textarea
              id="mcp-server-description"
              className="w-full max-w-2xl rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
              placeholder="e.g. Query and analyze PostgreSQL databases"
              rows={4}
              maxLength={500}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>MCP Server Instructions</Label>
            <p className="text-muted-foreground text-sm">
              Please go to{" "}
              <Link
                to={`${getKnowledgeBasePath(brandTheme)}?integration=${server.slug}`}
                className="text-primary hover:text-primary/80 hover:underline"
              >
                {server.slug.replace(/^mcp_/, "")}.md
              </Link>
              {" "}to modify MCP server specific context and instructions.
            </p>
          </div>
        </div>

        {/* Connection Settings Section */}
        <div className="bg-card/30 rounded-lg p-6 space-y-5">
          <div>
            <Label>Connection Settings</Label>
            <p className="text-muted-foreground text-sm mt-1">
              Server URL and authentication configuration.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="mcp-server-url">Server URL</Label>
            <Input
              id="mcp-server-url"
              type="url"
              autoComplete="off"
              value={url}
              className="max-w-md"
              onChange={(e) => {
                setUrl(e.target.value);
                setUrlOrAuthChanged(true);
                setTestStatus({ status: "idle", message: null });
              }}
              placeholder="https://mcp.example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="mcp-server-auth-type">Auth Type</Label>
            <Select
              value={authType}
              onValueChange={(v) => {
                setAuthType(v);
                setApiKey("");
                setHeaders([createHeaderEntry()]);
                setUrlOrAuthChanged(true);
                setTestStatus({ status: "idle", message: null });
              }}
            >
              <SelectTrigger id="mcp-server-auth-type" className="max-w-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="API_KEY">Bearer Token</SelectItem>
                <SelectItem value="CUSTOM_HEADERS">Custom Headers</SelectItem>
                <SelectItem value="OAUTH">OAuth</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {authType === "API_KEY" && (
            <div className="space-y-2">
              <Label htmlFor="mcp-server-api-key">Bearer Token</Label>
              <div className="max-w-md">
                <SecretInput
                  id="mcp-server-api-key"
                  placeholder="Leave blank to keep current token"
                  value={apiKey}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    setTestStatus({ status: "idle", message: null });
                  }}
                />
              </div>
            </div>
          )}

          {authType === "CUSTOM_HEADERS" && (
            <div className="space-y-2" role="group" aria-labelledby="mcp-server-headers-label">
              <Label id="mcp-server-headers-label">Headers</Label>
              <p className="text-xs text-muted-foreground">Leave blank to keep current headers.</p>
              {headers.map((header, idx) => (
                <div key={header.id} className="flex gap-2 max-w-lg">
                  <input
                    type="text"
                    autoComplete="off"
                    className="w-2/5 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    aria-label={`Header ${idx + 1} name`}
                    placeholder="Header name"
                    value={header.key}
                    onChange={(e) => {
                      setHeaders((h) => h.map((entry, i) => (i === idx ? { ...entry, key: e.target.value } : entry)));
                      setTestStatus({ status: "idle", message: null });
                    }}
                  />
                  <div className="relative flex-1">
                    <input
                      type={showSecrets ? "text" : "password"}
                      autoComplete="off"
                      className="w-full rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm"
                      aria-label={`Header ${idx + 1} value`}
                      placeholder="Value"
                      value={header.value}
                      onChange={(e) => {
                        setHeaders((h) => h.map((entry, i) => (i === idx ? { ...entry, value: e.target.value } : entry)));
                        setTestStatus({ status: "idle", message: null });
                      }}
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setShowSecrets((s) => !s)}
                      tabIndex={-1}
                    >
                      {showSecrets ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                    </button>
                  </div>
                  {headers.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2"
                      onClick={() => {
                        setHeaders((h) => h.filter((_, i) => i !== idx));
                        setTestStatus({ status: "idle", message: null });
                      }}
                    >
                      <Trash className="size-3.5" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setHeaders((h) => [...h, createHeaderEntry()])}
              >
                <Plus className="size-3.5 mr-1" />
                Add Header
              </Button>
              {hasIncompleteHeaders && (
                <p className="text-xs text-destructive">Each header must have both a name and a value.</p>
              )}
            </div>
          )}

          {connectionChanged && testStatus.status !== "success" && authType !== "OAUTH" && (
            <p className="text-xs text-yellow-500">
              Connection settings changed. Test Connection is required before saving.
            </p>
          )}
          {authType === "OAUTH" && connectionChanged && (
            <p className="text-xs text-muted-foreground">
              Saving will restart the OAuth flow. You'll be redirected to the provider.
            </p>
          )}

          {authType === "OAUTH" && !server.isOauthAuthorized && (
            <McpOauthAdvancedSettings
              url={url}
              selectedMethod={oauthMethodOverride}
              onMethodChange={setOauthMethodOverride}
              serverId={server.id}
            />
          )}
        </div>

        {/* Test Connection Result */}
        {testStatus.status === "success" && (
          <div className="flex items-center gap-2 rounded-md border border-green-500/30 bg-green-500/10 px-3 py-2 max-w-lg">
            <CheckCircle className="size-4 text-green-500 flex-shrink-0" />
            <span className="text-sm text-green-500">{testStatus.message}</span>
          </div>
        )}
        {testStatus.status === "failed" && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 max-w-lg">
            <p className="text-sm text-destructive">{testStatus.message}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => navigate(MCP_SERVERS_PATH)}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleTestConnection}
            disabled={testStatus.status === "testing" || !url || hasIncompleteHeaders}
          >
            <RefreshCw className={`size-3.5 mr-1.5 ${testStatus.status === "testing" ? "animate-spin" : ""}`} />
            {testStatus.status === "testing" ? "Testing..." : "Test Connection"}
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={isUpdating || !canSave}
          >
            {isUpdating ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Unsaved changes confirmation dialog */}
      <Dialog open={showUnsavedDialog} onOpenChange={(open) => {
        if (!open) {
          setShowUnsavedDialog(false);
          blocker.reset?.();
        }
      }}>
        <DialogContent variant="medium">
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes that will be lost if you leave this page. Are you sure you want to continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowUnsavedDialog(false);
                blocker.reset?.();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowUnsavedDialog(false);
                blocker.proceed?.();
              }}
            >
              Leave Page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
